import axios from "axios";
import {
  CardStatusResponse,
  SumSubTokenResponse,
  CreditDelegationRequest,
  CreditDelegationResponse,
  LinkCardRequest,
  UserCardsResponse,
  CreditInformationResponse,
  CreateCardResponse,
  CardSecretResponse,
} from "./types";
import { getApiClient } from "./httpClient";
import {
  formatAvailableLimit,
  mapEulerMetadataToSuppliedAssets,
} from "./utils";
import {
  getMockCardStatus,
  getMockUserCards,
  getMockAaveCreditInformation,
  getMockEulerCreditInformation,
  getMockSumSubToken,
  getMockCreditDelegationTxData,
} from "./mockData";

export const getCardStatus = async (
  eoaAddress: string
): Promise<CardStatusResponse> => {
  // Return mock data
  // return getMockCardStatus();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<CardStatusResponse>(
      `/cards/user/${eoaAddress}/status`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { data: "userNotExisting" };
    }
    throw error;
  }
};

export const getUserCards = async (
  eoaAddress: string
): Promise<UserCardsResponse> => {
  // Return mock data
  // return getMockUserCards();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<UserCardsResponse>(
      `/cards/user/${eoaAddress}`
    );
    return response.data;
  } catch (err) {
    console.error("[getUserCards]", err);
    return { data: null };
  }
};

export const getCreditInformation = async (
  creditAddress: string,
  creditType: "aave-v3" | "euler"
): Promise<CreditInformationResponse> => {
  // Return mock data based on credit type
  // if (creditType === "aave-v3") {
  //   return getMockAaveCreditInformation();
  // } else {
  //   return getMockEulerCreditInformation();
  // }

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<CreditInformationResponse>(
      `/cards/credit/${creditAddress}/${creditType}`
    );

    // Transform availableLimit if it exists
    if (
      response.data?.data &&
      typeof response.data.data.availableLimit === "number"
    ) {
      response.data.data.availableLimit = formatAvailableLimit(
        response.data.data.availableLimit,
        creditType
      );
      response.data.data.amountDelegated = formatAvailableLimit(
        response.data.data.amountDelegated,
        creditType
      );
    }

    // If Euler, map vaults to suppliedAssets for compatibility (without mutating original)
    if (creditType === "euler" && response.data?.data?.metadata) {
      response.data.data.metadata = mapEulerMetadataToSuppliedAssets(
        response.data.data.metadata
      );
    }

    return response.data;
  } catch (error) {
    console.error("[getCreditInformation]", error);
    return { data: null };
  }
};

export const getSumSubAccessToken = async (
  userId: string,
  ipAddress?: string
): Promise<SumSubTokenResponse["data"] | undefined> => {
  // Return mock data
  // return getMockSumSubToken();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.post<SumSubTokenResponse>(
      "/cards/sumsub/token",
      { userId, ipAddress }
    );
    return response.data.data;
  } catch (error) {
    console.error("[error at getSumSubAccessToken]", error);
    return undefined;
  }
};

export const getCreditDelegationTxData = async (
  requestData: CreditDelegationRequest
): Promise<{ data: CreditDelegationResponse }> => {
  // Return mock data
  // return getMockCreditDelegationTxData();

  // Real API call (commented out - uncomment to use real API)
  const client = getApiClient();
  const response = await client.post<{ data: CreditDelegationResponse }>(
    "/cards/credit/delegate",
    requestData
  );
  return response.data;
};

export const linkCardToCreditAccount = async (
  requestData: LinkCardRequest
): Promise<any> => {
  const client = getApiClient();
  const response = await client.patch("/cards/", requestData);
  return response.data;
};

export const createCard = async (
  userEoa: string
): Promise<CreateCardResponse> => {
  const client = getApiClient();
  const response = await client.post<CreateCardResponse>(
    `/card/create/${userEoa}`
  );
  return response.data;
};

export const freezeCard = async (
  userEoa: string,
  cardID: string,
  freeze: boolean
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();
  try {
    const response = await client.patch(
      `/cards/user/${userEoa}/status/${cardID}`,
      {
        cardID,
        freeze,
      }
    );
    // if 200 then return success
    if (response.status === 200) {
      return { success: true };
    }
    // if not 200, return failure with message
    return {
      success: false,
      message: response.data?.message || "Failed to freeze card",
    };
  } catch (error: any) {
    console.error("[freezeCard]", error);
    return {
      success: false,
      message: error?.message || "Failed to freeze card",
    };
  }
};

export const updatePin = async (
  userEoa: string,
  cardID: string,
  iv: string, // base64 encoded IV
  data: string // base64 encoded encrypted pin
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();
  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/user/${userEoa}/secrets/${cardID}/pin`,
      { iv, data }
    );
    return response.data;
  } catch (error) {
    console.error("[updatePin]", error);
    return { success: false, message: "Failed to update PIN" };
  }
};

/**
 * Get the public key for card encryption
 * @returns Public key in PEM format
 */
export const getCardPublicKey = async (): Promise<{ data: string }> => {
  const client = getApiClient();
  const response = await client.get<{ data: string }>("/cards/pubkey");
  return response.data;
};

/**
 * Get encrypted card details for a user
 * @param eoaAddress Ethereum address of the user
 * @param cardID UUID of the card
 * @param sessionID Session identifier
 * @returns Encrypted card details
 */
export const getCardSecrets = async (
  eoaAddress: string,
  cardID: string,
  sessionID: string
): Promise<CardSecretResponse> => {
  const client = getApiClient();
  const response = await client.get<{
    data: {
      encryptedPan: {
        iv: string;
        data: string;
      };
      encryptedCvc: {
        iv: string;
        data: string;
      };
    };
  }>(`/cards/user/${eoaAddress}/secrets/${cardID}`, {
    headers: {
      sessionID,
    },
  });
  return response.data;
};
